import React, { useEffect, useLayoutEffect, useState } from "react";
import {
  AutocompleteInput,
  FormDataConsumer,
  ReferenceInput,
  required,
  SelectInput,
  TextInput,
  useDataProvider,
  useNotify,
  Labeled,
  Filter,
  NumberInput,
  number,
  useTranslate,
  useResourceContext
} from "react-admin";

import { MarkdownField, MarkdownInput } from '@react-admin/ra-markdown';


import { Box, Link, Typography } from "@mui/material";
import { DateInput } from "@react-admin/ra-form-layout";

import { DateTimeInput } from "@react-admin/ra-form-layout";

import {
  DesktopDatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterMomentHijri } from "@mui/x-date-pickers/AdapterMomentHijri"; // Use Hijri Date Adapter
import hMoment from "moment-hijri"; // Hijri moment.js under alias
import moment from "moment-timezone";
import { useInput } from "react-admin";

import { Button } from "@mui/material";
import "handsontable/dist/handsontable.full.css";
import { debounce } from "lodash"; // Ensure lodash is installed
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { CarPlateField, CarPlateInput } from "../CarePlate";



export const DriverNIDInput = ({ source, onDriverNotFound }) => {
  const { setValue, getValues, control } = useFormContext();

  const notify = useNotify();
  const dataProvider = useDataProvider();

  const [operationPlans, setOperationPlans] = useState([]);
  const [nidNumber, setNidNumber] = useState("");
  const [periodData, setPeriodData] = useState(null);
  const [previousCategory, setPreviousCategory] = useState(null);

  const isSearchDone = useWatch({
    control,
    name: "is_search_done",
    defaultValue: false,
  });

  const workflowCategory = useWatch({
    control,
    name: "workflow_category",
    defaultValue: null,
  });

  const nid_number = getValues(source);

  // Clear search results when workflow category changes
  useEffect(() => {
    if (workflowCategory !== previousCategory && previousCategory !== null) {
      // Category has actually changed
      if (isSearchDone) {
        resetFormFieldsKeepCategory();
        setOperationPlans([]);
        setNidNumber("");
      }
      // Clear NID input field when category changes
      setValue(source, "");
    }
    setPreviousCategory(workflowCategory);
  }, [workflowCategory, previousCategory, isSearchDone, setValue, source]);

  // Function to reset only when category changes (preserve category selection)
  const resetFormFieldsKeepCategory = () => {
    const currentCategory = getValues("workflow_category");
    setValue("selected_plan_id", null);
    setValue("operation_plan", []);
    setValue("is_ods", "notcorrect");
    setValue("transport_type", null);
    setValue("num_of_seats", null);
    setValue("manufacturer_name", null);
    setValue("vehicle_model", null);
    setValue("education_area_id", null);
    setValue("education_zone_id", null);
    setValue("education_level", null);
    setValue("make_year", null);
    setValue("gender", null);
    setValue("vendor_id", null);
    setValue("contract_id", null);
    setValue("contract_type", null);
    setValue("disability_type", null);
    setValue("service_type", null);
    setValue("contract_number", null);
    setValue("opname", null);
    setValue("operator", null);
    setValue("sub_operator", null);
    setValue("yearmodel", null);
    setValue("brand", null);
    setValue("capacity", null);
    setValue("deviceimei", null);
    setValue("operation_card_expiration_date", null);
    setValue("is_sub_contractor", null);
    setValue("driver_name", null);
    setValue("escort_nid_number", null);
    setValue("plate_number", null);
    setValue("driver_nationality", null);
    setValue("driver_dob_gregorian", null);
    setValue("driver_dob_hijri", null);
    setValue("driver_age", null);
    setValue("driver_license_expiry_date", null);
    setValue("driver_license_type", null);
    setValue("driver_is_ods", "notcorrect");
    setValue("serial_number", null);
    setValue("operational_number", null);
    setValue("school_id", null, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    setValue("is_search_done", false);
    setValue(source, ""); // Clear the main NID source field
    setValue("workflow_category", currentCategory); // Restore category selection
  };

  // Validate Saudi National ID/Iqama
  const validateNID = (nid) => {
    if (!nid || nid.length !== 10) return false;
    const firstDigit = nid[0];
    return ['1', '2', '3'].includes(firstDigit) && /^\d{10}$/.test(nid);
  };

  // Function to reset form fields
  const resetFormFields = () => {
    setValue("workflow_category", workflowCategory); // Keep the current workflow category
    setValue("selected_plan_id", null);
    setValue("operation_plan", []);
    setValue("is_ods", "notcorrect");
    setValue("transport_type", null);
    setValue("num_of_seats", null);
    setValue("manufacturer_name", null);
    setValue("vehicle_model", null);
    setValue("education_area_id", null);
    setValue("education_zone_id", null);
    setValue("education_level", null);
    setValue("make_year", null);
    setValue("gender", null);
    setValue("vendor_id", null);
    setValue("contract_id", null);
    setValue("contract_type", null);
    setValue("disability_type", null);
    setValue("service_type", null);
    setValue("contract_number", null);
    setValue("opname", null);
    setValue("operator", null);
    setValue("sub_operator", null);
    setValue("yearmodel", null);
    setValue("brand", null);
    setValue("capacity", null);
    setValue("deviceimei", null);
    setValue("operation_card_expiration_date", null);
    setValue("is_sub_contractor", null);
    setValue("driver_name", null);
    //setValue("driver_nid", null);
    setValue("escort_nid_number", null);
    setValue("plate_number", null);

    setValue("driver_nationality", null);
    setValue("driver_dob_gregorian", null);
    setValue("driver_dob_hijri", null);
    setValue("driver_age", null);
    setValue("driver_license_expiry_date", null);
    setValue("driver_license_type", null);
    setValue("driver_is_ods", "notcorrect");
    setValue("serial_number", null);
    setValue("operational_number", null);
    setValue("num_of_seats", null);
    setValue("manufacturer_name", null);
    setValue("vehicle_model", null);
    setValue("education_area_id", null);
    setValue("education_zone_id", null);
    setValue("education_level", null);
    setValue("make_year", null);
    setValue("contract_number", null);
    setValue("opname", null);
    setValue("operator", null);
    setValue("sub_operator", null);
    setValue("yearmodel", null);
    setValue("brand", null);
    setValue("capacity", null);
    setValue("deviceimei", null);
    setValue("operation_card_expiration_date", null);
    setValue("is_sub_contractor", null);
    setValue("school_id", null, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    // setPlateNumber("");
    setValue("is_search_done", false);
    setValue(source, ""); // Clear the main NID source field
  };

  // useEffect to update form values when periodData, operationPlans, and nidNumber are set
  useEffect(() => {
    if (periodData) {
      setValue("period_id", periodData.id);
      setValue("academic_year", periodData.academic_year);
    }

    if (operationPlans.length > 0 && nidNumber) {
      setValue("operation_plan", operationPlans);
      setValue("selected_plan_id", null);
      setValue("is_ods", "notcorrect");
      setValue("driver_is_ods", "notcorrect");
    }
  }, [periodData, operationPlans, nidNumber, setValue]);

  // Handle search functionality
  const handleSearch = async () => {
    const nid = getValues(source);
    const category = getValues("workflow_category");

    if (!category) {
      notify("يرجى اختيار نوع البحث (سائق أو مرافق)", { type: "error" });
      return;
    }

    if (nid) {
      // Validate Saudi National ID/Iqama
      if (!validateNID(nid)) {
        notify("رقم الهوية يجب أن يكون 10 أرقام ويبدأ بـ 1 أو 2 أو 3", { type: "error" });
        return;
      }

      try {
        const ticket_date = getValues("accident_time");

        const periodResponse = await dataProvider.getList("ods_operational_periods", {
          filter: {
            "from_date@_lte": ticket_date,
            "lead_to_date@_gte": ticket_date,
          },
          pagination: { page: 1, perPage: 1 },
        });

        const periodData = periodResponse.data;
        setPeriodData(periodData.length > 0 ? periodData[0] : null);
      } catch (error) {
        console.log(error);
        notify("فشل التعرف تلقائيا علي الفترة الحالية", { type: "error" });
      }

      try {
        // Determine search field based on workflow category
        const searchField = category === "1" ? "nid_number" : "escort_nid_number";
        const filterCondition = category === "1" 
          ? { "nid_number@_eq": nid }
          : { "escort_nid_number@_eq": nid };

        // Get latest record by NID (driver or escort)
        const response = await dataProvider.getList("ods_plan_history", {
          filter: filterCondition,
          pagination: { page: 1, perPage: 1 },
          sort: { field: "id", order: "DESC" },
        });

        const data = response.data;

        if (data && data.length > 0) {
          const record = data[0];
          const recordIdentifier = category === "1" ? record.nid_number : record.escort_nid_number;
          
          if (record.sub_day && recordIdentifier) {

            // Check for existing accidents with same NID
            const accidentFilter = category === "1" 
              ? { "driver_nid@_eq": record.driver_nid || nid, "accident_time@_gte": record.sub_day }
              : { "driver_nid@_eq": nid, "accident_time@_gte": record.sub_day };

            const accidentCountResponse = await dataProvider.getList("blacklist_drivers_log", {
              filter: accidentFilter,
              pagination: { page: 1, perPage: 1 },
              meta: {
                aggregate: ["count"],
              },
            });

            const accidentCount = accidentCountResponse.total;

            const personType = category === "1" ? "السائق" : "المرافق";
            if (accidentCount > 0)
              notify(`تنبيه : يوجد تذاكر سابقة لهذا ${personType} في نفس اليوم`, {
                type: "warning", autoHideDuration: 20000,
              });

            // Get operation plans for this person by sub_day and identifier
            const operationPlansFilter = category === "1" 
              ? {
                  "sub_day@_eq": record.sub_day,
                  "nid_number@_eq": record.nid_number,
                }
              : {
                  "sub_day@_eq": record.sub_day,
                  "escort_nid_number@_eq": record.escort_nid_number,
                };

            const operationPlansResponse = await dataProvider.getList("ods_plan_history", {
              filter: operationPlansFilter,
              pagination: { page: 1, perPage: 10 },
            });

            const operationPlans = operationPlansResponse.data;

            setValue("selected_plan_id", null);
            setValue("is_ods", "notcorrect");
            setValue("transport_type", null);
            setValue("num_of_seats", null);
            setValue("manufacturer_name", null);
            setValue("vehicle_model", null);
            setValue("education_area_id", null);
            setValue("education_zone_id", null);
            setValue("education_level", null);
            setValue("make_year", null);
            setValue("gender", null);
            setValue("vendor_id", null);
            setValue("contract_id", null);
            setValue("contract_type", null);
            setValue("disability_type", null);
            setValue("service_type", null);
            setValue("plate_number", null);
            setValue("contract_number", null);
            setValue("opname", null);
            setValue("operator", null);
            setValue("sub_operator", null);
            setValue("yearmodel", null);
            setValue("brand", null);
            setValue("capacity", null);
            setValue("deviceimei", null);
            setValue("operation_card_expiration_date", null);
            setValue("is_sub_contractor", null);
            setValue("driver_name", null);
          //  setValue("driver_nid", null);
            setValue("driver_nationality", null);
            setValue("driver_dob_gregorian", null);
            setValue("driver_dob_hijri", null);
            setValue("driver_age", null);
            setValue("driver_license_expiry_date", null);
            setValue("driver_license_type", null);
            setValue("driver_is_ods", "notcorrect");
            setValue("serial_number", null);
            setValue("operational_number", null);
            setValue("num_of_seats", null);
            setValue("manufacturer_name", null);
            setValue("vehicle_model", null);
            setValue("education_area_id", null);
            setValue("education_zone_id", null);
            setValue("education_level", null);
            setValue("make_year", null);
            setValue("contract_number", null);
            setValue("opname", null);
            setValue("operator", null);
            setValue("sub_operator", null);
            setValue("yearmodel", null);
            setValue("brand", null);
            setValue("capacity", null);
            setValue("deviceimei", null);
            setValue("operation_card_expiration_date", null);
            setValue("is_sub_contractor", null);
            setValue("school_id", null, { shouldDirty: true, shouldValidate: true, shouldTouch: true });



            setOperationPlans(operationPlans);
            setNidNumber(recordIdentifier);
            setValue("is_search_done", true);

            // Set the appropriate NID field based on category
            if (category === "2") {
              setValue("escort_nid_number", nid);
            }

          }
        } else {
          const personType = category === "1" ? "السائق" : "المرافق";
          notify(`فشل التعرف علي ${personType} فى الخطة التشغيلية - يرجي اضافة البيانات يدويا.`, { type: "warning" });
          resetFormFields();
          if (onDriverNotFound) onDriverNotFound();
        }
      } catch (error) {
        console.log(error);
        const personType = category === "1" ? "السائق" : "المرافق";
        notify(`خطأ في جلب بيانات ${personType}`, { type: "error" });
      }
    } else {
      notify("فضلا ادخل رقم الهوية الوطنية أو الإقامة.", { type: "warning" });
    }
  };

  const handleResetSearch = () => {
    // Complete reset - clear everything including workflow category
    setValue("workflow_category", null);
    setValue("selected_plan_id", null);
    setValue("operation_plan", []);
    setValue("is_ods", "notcorrect");
    setValue("transport_type", null);
    setValue("num_of_seats", null);
    setValue("manufacturer_name", null);
    setValue("vehicle_model", null);
    setValue("education_area_id", null);
    setValue("education_zone_id", null);
    setValue("education_level", null);
    setValue("make_year", null);
    setValue("gender", null);
    setValue("vendor_id", null);
    setValue("contract_id", null);
    setValue("contract_type", null);
    setValue("disability_type", null);
    setValue("service_type", null);
    setValue("contract_number", null);
    setValue("opname", null);
    setValue("operator", null);
    setValue("sub_operator", null);
    setValue("yearmodel", null);
    setValue("brand", null);
    setValue("capacity", null);
    setValue("deviceimei", null);
    setValue("operation_card_expiration_date", null);
    setValue("is_sub_contractor", null);
    setValue("driver_name", null);
    setValue("escort_nid_number", null);
    setValue("plate_number", null);
    setValue("driver_nationality", null);
    setValue("driver_dob_gregorian", null);
    setValue("driver_dob_hijri", null);
    setValue("driver_age", null);
    setValue("driver_license_expiry_date", null);
    setValue("driver_license_type", null);
    setValue("driver_is_ods", "notcorrect");
    setValue("serial_number", null);
    setValue("operational_number", null);
    setValue("school_id", null, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    setValue("is_search_done", false);
    setValue(source, ""); // Clear the main NID source field
    
    // Reset local state
    setOperationPlans([]);
    setNidNumber("");
    setPreviousCategory(null); // Reset previous category tracking
  };

  return (
    <Box display="flex" alignItems="stretch" gap={2} width="100%">
      <HiddenInputForDisabledField name={"is_search_done"} control={control} />
      <HiddenInputForDisabledField name={source} control={control} />
      <HiddenInputForDisabledField name={"workflow_category"} control={control} />

      {/* Workflow Category Selection - always visible */}
      <SelectInput
        source="workflow_category"
        label="نوع البحث"
        choices={[
          { id: "1", name: "سائق" },
          { id: "2", name: "مرافق" }
        ]}
        validate={[required("يرجى اختيار نوع البحث")]}
        readOnly={isSearchDone}
        sx={{ flex: "0 0 20%" }}
      />

        {isSearchDone && (
          <>
            <TextInput
              source={source}
              label={workflowCategory === "1" ? "رقم الهوية الوطنية / الإقامة (السائق)" : "رقم الهوية الوطنية / الإقامة (المرافق)"}
              fullWidth
              readOnly
              validate={[
                (value) => validateNID(value) ? undefined : "رقم الهوية يجب أن يكون 10 أرقام ويبدأ بـ 1 أو 2 أو 3"
              ]}
              sx={{ flex: "1" }}
            />
          </>
        )}

        {!isSearchDone && (
          <>
            <TextInput
              source={source}
              label={workflowCategory === "1" ? "رقم الهوية الوطنية / الإقامة (السائق)" : workflowCategory === "2" ? "رقم الهوية الوطنية / الإقامة (المرافق)" : "رقم الهوية الوطنية / الإقامة"}
              fullWidth
              validate={[
                (value) => value && validateNID(value) ? undefined : "رقم الهوية يجب أن يكون 10 أرقام ويبدأ بـ 1 أو 2 أو 3"
              ]}
              helperText="أدخل رقم الهوية الوطنية أو الإقامة (10 أرقام تبدأ بـ 1 أو 2 أو 3)"
              inputProps={{ 
                maxLength: 10,
                pattern: "[1-3][0-9]{9}",
                inputMode: "numeric"
              }}
              sx={{ flex: "1" }}
              disabled={!workflowCategory}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              disabled={!workflowCategory}
              sx={{
                flex: "0 0 30%",
                height: "56px",
                whiteSpace: "nowrap",
              }}
            >
              {workflowCategory === "1" ? "الاستعلام عن بيانات السائق" : workflowCategory === "2" ? "الاستعلام عن بيانات المرافق" : "الاستعلام عن البيانات"}
          </Button>
        </>
      )}

      {isSearchDone && (
        <Typography variant="body2">
          <Link
            component="button"
            variant="body2"
            onClick={handleResetSearch}
            sx={{ cursor: "pointer", color: "blue" }}
          >
            إعادة البحث
          </Link>
        </Typography>
      )}

      <Box sx={{ flex: "1" }} />
    </Box>
  );
};


export const DriverNIDInputBlacklist = ({ source, onDriverNotFound }) => {
  const { setValue, getValues, control } = useFormContext();

  const notify = useNotify();
  const dataProvider = useDataProvider();

  const [operationPlans, setOperationPlans] = useState([]);
  const [nidNumber, setNidNumber] = useState("");
  const [periodData, setPeriodData] = useState(null);
  const [previousCategory, setPreviousCategory] = useState(null);

  const isSearchDone = useWatch({
    control,
    name: "is_search_done",
    defaultValue: false,
  });

  const workflowCategory = useWatch({
    control,
    name: "workflow_category",
    defaultValue: null,
  });

  const nid_number = getValues(source);

  // Clear search results when workflow category changes
  useEffect(() => {
    if (workflowCategory !== previousCategory && previousCategory !== null) {
      // Category has actually changed
      if (isSearchDone) {
        resetFormFieldsKeepCategory();
        setNidNumber("");
      }
      // Clear NID input field when category changes
      setValue(source, "");
    }
    setPreviousCategory(workflowCategory);
  }, [workflowCategory, previousCategory, isSearchDone, setValue, source]);

  // Function to reset only when category changes (preserve category selection)
  const resetFormFieldsKeepCategory = () => {
    const currentCategory = getValues("workflow_category");
    setValue("is_search_done", false);
    setValue(source, ""); // Clear the main NID source field
    setValue("workflow_category", currentCategory); // Restore category selection
  };

  // Validate Saudi National ID/Iqama
  const validateNID = (nid) => {
    if (!nid || nid.length !== 10) return false;
    const firstDigit = nid[0];
    return ['1', '2', '3'].includes(firstDigit) && /^\d{10}$/.test(nid);
  };

  // Function to reset form fields
  const resetFormFields = () => {
    setValue("workflow_category", workflowCategory); // Keep the current workflow category
    setValue("selected_plan_id", null);
    setValue("operation_plan", []);
  
    // setPlateNumber("");
    setValue("is_search_done", false);
    setValue(source, ""); // Clear the main NID source field
  };

  // useEffect to update form values when periodData, operationPlans, and nidNumber are set
  useEffect(() => {
   

    if (operationPlans.length > 0 && nidNumber) {
      setValue("operation_plan", operationPlans);
      setValue("selected_plan_id", null);
    }
  }, [periodData, operationPlans, nidNumber, setValue]);

  // Handle search functionality
  const handleSearch = async () => {
    const nid = getValues(source);
    const category = getValues("workflow_category");

    if (!category) {
      notify("يرجى اختيار نوع البحث (سائق أو مرافق)", { type: "error" });
      return;
    }

    if (nid) {
      // Validate Saudi National ID/Iqama
      if (!validateNID(nid)) {
        notify("رقم الهوية يجب أن يكون 10 أرقام ويبدأ بـ 1 أو 2 أو 3", { type: "error" });
        return;
      }

      try {
        // Search for existing blacklist records with same NID, workflow_category, and workflow_subcategory = 5
        const blacklistFilter = {
          "driver_nid@_eq": nid,
          "workflow_category@_eq": category,
          "workflow_subcategory@_eq": 5
        };

        const blacklistResponse = await dataProvider.getList("blacklist_drivers_log", {
          filter: blacklistFilter,
          pagination: { page: 1, perPage: 10 },
        });

        const blacklistRecords = blacklistResponse.data || [];
        const personType = category === "1" ? "السائق" : "المرافق";

        // Show alert only if no existing records found
        if (blacklistRecords.length === 0) {
          notify(`تنبيه : لا يوجد تذاكر استبعاد سابقة لهذا ${personType}`, {
            type: "warning", 
            autoHideDuration: 20000,
          });
          // Don't set is_search_done if no records found
          return;
        }

        // Only set values if records are found
        setNidNumber(nid);
        setValue("is_search_done", true);
        
        // Set the driver_nid field
        setValue("driver_nid", nid);

        // Set the appropriate NID field based on category
        if (category === "1") {
          setValue("nid_number", nid);
        } else if (category === "2") {
          setValue("escort_nid_number", nid);
        }

      } catch (error) {
        console.log(error);
        const personType = category === "1" ? "السائق" : "المرافق";
        notify(`خطأ في جلب بيانات ${personType}`, { type: "error" });
      }
    } else {
      notify("فضلا ادخل رقم الهوية الوطنية أو الإقامة.", { type: "warning" });
    }
  };

  const handleResetSearch = () => {
    // Complete reset - clear everything including workflow category
    setValue("workflow_category", null);

    setValue("is_search_done", false);
    setValue(source, ""); // Clear the main NID source field
 
    setNidNumber("");
    setPreviousCategory(null); // Reset previous category tracking
  };

  return (
    <Box display="flex" alignItems="stretch" gap={2} width="100%">
      <HiddenInputForDisabledField name={"is_search_done"} control={control} />
      <HiddenInputForDisabledField name={source} control={control} />
      <HiddenInputForDisabledField name={"workflow_category"} control={control} />

      {/* Workflow Category Selection - always visible */}
      <SelectInput
        source="workflow_category"
        label="نوع البحث"
        choices={[
          { id: "1", name: "سائق" },
          { id: "2", name: "مرافق" }
        ]}
        validate={[required("يرجى اختيار نوع البحث")]}
        readOnly={isSearchDone}
        sx={{ flex: "0 0 20%" }}
      />

        {isSearchDone && (
          <>
            <TextInput
              source={source}
              label={workflowCategory === "1" ? "رقم الهوية الوطنية / الإقامة (السائق)" : "رقم الهوية الوطنية / الإقامة (المرافق)"}
              fullWidth
              readOnly
              validate={[
                (value) => validateNID(value) ? undefined : "رقم الهوية يجب أن يكون 10 أرقام ويبدأ بـ 1 أو 2 أو 3"
              ]}
              sx={{ flex: "1" }}
            />
          </>
        )}

        {!isSearchDone && (
          <>
            <TextInput
              source={source}
              label={workflowCategory === "1" ? "رقم الهوية الوطنية / الإقامة (السائق)" : workflowCategory === "2" ? "رقم الهوية الوطنية / الإقامة (المرافق)" : "رقم الهوية الوطنية / الإقامة"}
              fullWidth
              validate={[
                (value) => value && validateNID(value) ? undefined : "رقم الهوية يجب أن يكون 10 أرقام ويبدأ بـ 1 أو 2 أو 3"
              ]}
              helperText="أدخل رقم الهوية الوطنية أو الإقامة (10 أرقام تبدأ بـ 1 أو 2 أو 3)"
              inputProps={{ 
                maxLength: 10,
                pattern: "[1-3][0-9]{9}",
                inputMode: "numeric"
              }}
              sx={{ flex: "1" }}
              disabled={!workflowCategory}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              disabled={!workflowCategory}
              sx={{
                flex: "0 0 30%",
                height: "56px",
                whiteSpace: "nowrap",
              }}
            >
              {workflowCategory === "1" ? "الاستعلام عن بيانات السائق" : workflowCategory === "2" ? "الاستعلام عن بيانات المرافق" : "الاستعلام عن البيانات"}
          </Button>
        </>
      )}

      {isSearchDone && (
        <Typography variant="body2">
          <Link
            component="button"
            variant="body2"
            onClick={handleResetSearch}
            sx={{ cursor: "pointer", color: "blue" }}
          >
            إعادة البحث
          </Link>
        </Typography>
      )}

      <Box sx={{ flex: "1" }} />
    </Box>
  );
};

export const VehiclePlateInput = ({ source, onVehicleNotFound }) => {
  const { setValue, getValues, control } = useFormContext();


  const notify = useNotify();
  const dataProvider = useDataProvider();

  const [operationPlans, setOperationPlans] = useState([]);
  const [plateNumber, setPlateNumber] = useState("");
  const [periodData, setPeriodData] = useState(null);  // Track period data separately

  const isSearchDone = useWatch({
    control,
    name: "is_search_done",
    defaultValue: false,
  });

  // Determine if we are in edit mode
  const plate_number = getValues(source);
  const accident_date = getValues("accident_time");

  // Set isSearchDone based on conditions


  // Function to reset form fields
  const resetFormFields = () => {
    setValue("selected_plan_id", null);
    setValue("operation_plan", []);
    setValue("is_ods", "notcorrect");
    setValue("transport_type", null);
    setValue("num_of_seats", null);
    setValue("manufacturer_name", null);
    setValue("vehicle_model", null);
    setValue("education_area_id", null);
    setValue("education_zone_id", null);
    setValue("education_level", null);
    setValue("make_year", null);
    setValue("gender", null);
    setValue("vendor_id", null);
    setValue("contract_id", null);
    setValue("contract_type", null);
    setValue("disability_type", null);
    setValue("service_type", null);
    // setValue("plate_number", null);
    setValue("contract_number", null);
    setValue("opname", null);
    setValue("operator", null);
    setValue("sub_operator", null);
    setValue("yearmodel", null);
    setValue("brand", null);
    setValue("capacity", null);
    setValue("deviceimei", null);
    setValue("operation_card_expiration_date", null);
    setValue("is_sub_contractor", null);
    setValue("driver_name", null);
    setValue("driver_nid", null);
    setValue("driver_nationality", null);
    setValue("driver_dob_gregorian", null);
    setValue("driver_dob_hijri", null);
    setValue("driver_age", null);
    setValue("driver_license_expiry_date", null);
    setValue("driver_license_type", null);
    setValue("driver_is_ods", "notcorrect");
    setValue("serial_number", null);
    setValue("operational_number", null);
    setValue("num_of_seats", null);
    setValue("manufacturer_name", null);
    setValue("vehicle_model", null);
    setValue("education_area_id", null);
    setValue("education_zone_id", null);
    setValue("education_level", null);
    setValue("make_year", null);
    setValue("contract_number", null);
    setValue("opname", null);
    setValue("operator", null);
    setValue("sub_operator", null);
    setValue("yearmodel", null);
    setValue("brand", null);
    setValue("capacity", null);
    setValue("deviceimei", null);
    setValue("operation_card_expiration_date", null);
    setValue("is_sub_contractor", null);
    setValue("school_id", null, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    // setPlateNumber("");
    setValue("is_search_done", false);

    // Reset other fields as necessary...

  };





  // useEffect to update form values when periodData, operationPlans, and plateNumber are set
  useEffect(() => {

    if (periodData) {
      setValue("period_id", periodData.id);
      setValue("academic_year", periodData.academic_year);
    }

    if (operationPlans.length > 0 && plateNumber) {
      console.log('c');
      setValue("operation_plan", operationPlans);

      setValue("selected_plan_id", null);
      //  setValue("transport_type", "bus");
      //setValue("plate_number", plateNumber);
      setValue("is_ods", "notcorrect");
      setValue("driver_is_ods", "notcorrect");
    }
  }, [periodData, operationPlans, plateNumber, setValue]);




  // Handle search functionality
  const handleSearch = async () => {
    const plate = getValues(source);
    const accident_date = getValues("accident_time");

    if (plate && accident_date) {

      let modifiedPlate = plate.replace(/ه/g, "هـ");

      try {
        const periodResponse = await dataProvider.getList("ods_operational_periods", {
          filter: {
            "from_date@_lte": accident_date,
            "lead_to_date@_gte": accident_date,
          },
          pagination: { page: 1, perPage: 1 },
        });

        const periodData = periodResponse.data;
        setPeriodData(periodData.length > 0 ? periodData[0] : null);
      } catch (error) {
        console.log(error);
        notify("فشل التعرف تلقائيا علي الفترة من تاريخ الحادث", { type: "error" });
      }

      try {
        const response = await dataProvider.getList("ods_plan_history", {
          filter: {
            "plateletters@_eq": modifiedPlate.split(" ").slice(1).join(" "),
            "platenumbers@_eq": modifiedPlate.split(" ")[0],
            "sub_day@_lte": accident_date,
          },
          pagination: { page: 1, perPage: 1 },
          sort: { field: "id", order: "DESC" },
        });

        const data = response.data;

        if (data && data.length > 0) {
          const Vehicle = data[0];
          if (Vehicle.sub_day) {

            // Assuming 'accident_date' is a JavaScript Date object or ISO String
            const startOfDay = new Date(accident_date);
            startOfDay.setHours(0, 0, 0, 0); // sets to beginning of the day (00:00:00)

            const endOfDay = new Date(accident_date);
            endOfDay.setHours(23, 59, 59, 999); // sets to end of the day (23:59:59.999)

            const accidentCountResponse = await dataProvider.getList("accidents_accident_log", {
              filter: {
                "accident_time@_gte": startOfDay.toISOString(),
                "accident_time@_lte": endOfDay.toISOString(),
                "plate_number@_eq": plate,
              },
              pagination: { page: 1, perPage: 1 },
              meta: {
                aggregate: ["count"],
              },
            });

            const accidentCount = accidentCountResponse.total; // or use accidentCountResponse.data.length if aggregate not supported

            if (accidentCount > 0)
              notify("تنبيه : يوجد حادث بالفعل فى نفس اليوم لنفس الحافلة", {
                type: "warning", autoHideDuration: 20000, // duration in milliseconds (10 seconds)
              });






            const operationPlansResponse = await dataProvider.getList("ods_plan_history", {
              filter: {
                "sub_day@_eq": Vehicle.sub_day,
                "plateletters@_eq": modifiedPlate.split(" ").slice(1).join(" "),
                "platenumbers@_eq": modifiedPlate.split(" ")[0],
              },
              pagination: { page: 1, perPage: 10 },
            });

            const operationPlans = operationPlansResponse.data;

            setValue("selected_plan_id", null);
            setValue("is_ods", "notcorrect");
            setValue("transport_type", null);
            setValue("num_of_seats", null);
            setValue("manufacturer_name", null);
            setValue("vehicle_model", null);
            setValue("education_area_id", null);
            setValue("education_zone_id", null);
            setValue("education_level", null);
            setValue("make_year", null);
            setValue("gender", null);
            setValue("vendor_id", null);
            setValue("contract_id", null);
            setValue("contract_type", null);
            setValue("disability_type", null);
            setValue("service_type", null);
            // setValue("plate_number", null);
            setValue("contract_number", null);
            setValue("opname", null);
            setValue("operator", null);
            setValue("sub_operator", null);
            setValue("yearmodel", null);
            setValue("brand", null);
            setValue("capacity", null);
            setValue("deviceimei", null);
            setValue("operation_card_expiration_date", null);
            setValue("is_sub_contractor", null);
            setValue("driver_name", null);
            setValue("driver_nid", null);
            setValue("driver_nationality", null);
            setValue("driver_dob_gregorian", null);
            setValue("driver_dob_hijri", null);
            setValue("driver_age", null);
            setValue("driver_license_expiry_date", null);
            setValue("driver_license_type", null);
            setValue("driver_is_ods", "notcorrect");
            setValue("serial_number", null);
            setValue("operational_number", null);
            setValue("num_of_seats", null);
            setValue("manufacturer_name", null);
            setValue("vehicle_model", null);
            setValue("education_area_id", null);
            setValue("education_zone_id", null);
            setValue("education_level", null);
            setValue("make_year", null);
            setValue("contract_number", null);
            setValue("opname", null);
            setValue("operator", null);
            setValue("sub_operator", null);
            setValue("yearmodel", null);
            setValue("brand", null);
            setValue("capacity", null);
            setValue("deviceimei", null);
            setValue("operation_card_expiration_date", null);
            setValue("is_sub_contractor", null);
            setValue("school_id", null, { shouldDirty: true, shouldValidate: true, shouldTouch: true });



            setOperationPlans(operationPlans);
            setPlateNumber(plate);
            setValue("is_search_done", true);

          }
        } else {
          notify("فشل التعرف علي الحافلة فى الخطة التشغيلية - يرجي اضافة الحافلة يدويا.", { type: "warning" });
          resetFormFields();
          if (onVehicleNotFound) onVehicleNotFound();
        }
      } catch (error) {
        console.log(error);
        notify("Error fetching Vehicle data", { type: "error" });
      }
    } else {
      notify("فضلا ادخل رقم معلومات اللوحة الخاصة بالحافلة واختر يوم الحادث.", { type: "warning" });
    }
  };



  const handleResetSearch = () => {
    resetFormFields();
  };

  return (
    <Box display="flex" alignItems="stretch" gap={2} width="100%">
      <HiddenInputForDisabledField name={"is_search_done"} control={control} />
      <HiddenInputForDisabledField name={source} control={control} />

      {isSearchDone && (
        <>
          <CarPlateField
            source={source}
            label="رقم اللوحة"
            fullWidth
            arabic
            type={"transport"}
            onChange={(newValue) => console.log("Updated Value:", newValue)}
            sx={{ flex: "0 0 30%" }}
          />
        </>

      )}

      {!isSearchDone && (
        <>
          <CarPlateInput
            source={source}
            label="رقم اللوحة"
            fullWidth
            arabic
            type={"transport"}
            onChange={(newValue) => console.log("Updated Value:", newValue)}
            sx={{ flex: "0 0 30%" }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            sx={{
              flex: "0 0 40%",
              height: "56px",
              whiteSpace: "nowrap",
            }}
          >
            الاستعلام عن بيانات الحافلة
          </Button>
        </>
      )}

      {isSearchDone && (
        <Typography variant="body2">
          <Link
            component="button"
            variant="body2"
            onClick={handleResetSearch}
            sx={{ cursor: "pointer", color: "blue" }}
          >
            إعادة البحث
          </Link>
        </Typography>
      )}

      <Box sx={{ flex: "1" }} />
    </Box>
  );
};

const handleVehicleNotFound = () => {
  //setOpenCreateVehicleDialog(true);
};



export const useWatchMainChanged = (watched_column) => {
  const { control } = useFormContext();

  // Watch the 'editingVehicleMeta' field
  const IsOdsMain = useWatch({
    control,
    name: watched_column,
    defaultValue: "notcorrect",
  });

  return {
    disabled: IsOdsMain == "correct", // Disable input if 'editingVehicleMeta' is false
    control,
  };
};


export const useWatchPlateSearched = () => {
  const { control } = useFormContext();

  // Watch the 'editingVehicleMeta' field
  const IsSearched = useWatch({
    control,
    name: "is_search_done",
    defaultValue: false,
  });

  return {
    disabled: IsSearched == true, // Disable input if 'editingVehicleMeta' is false
    control,
  };
};
export const ReferenceFieldInputSearchNewWatch = ({
  field_meta,
  component,
  index,
  css_class,
  full_width,
  is_required,
  source_name,
  watched_column,
}) => {
  let extraProps = JSON.parse(field_meta);
  const { disabled } = useWatchMainChanged(watched_column); // Use the custom hook


  component = (
    <FormDataConsumer
      source={source_name}
      fullWidth={full_width}
      className={`input-field ${css_class}  `}
    >
      {({ formData, ...rest }) => (
        <>
          {!extraProps.extra_filter_column &&
            formData[extraProps.filter_column_exist] && (
              <ReferenceInput
                source={source_name}
                reference={extraProps.reference_resource}
                key={index}
                perPage={extraProps.limit}
                fullWidth
                filter={{
                  [extraProps.filter_key]: formData[extraProps.filter_column],
                }}
                {...rest}
              >
                <AutocompleteInput
                  fullWidth
                  optionText={extraProps.reference_source}
                  readOnly={disabled}
                  validate={is_required ? [required()] : undefined}
        filterToQuery={(searchText) => ({
          [`${extraProps.reference_source}@_ilike`]: `%${searchText}%`,
        })}
                  
                />
              </ReferenceInput>
            )}
          {extraProps.extra_filter_column &&
            formData[extraProps.filter_column_exist] && (
              <ReferenceInput
                source={source_name}
                reference={extraProps.reference_resource}
                key={index}
                perPage={extraProps.limit}
                filter={{
                  [extraProps.filter_key]: formData[extraProps.filter_column],
                  [extraProps.extra_filter_column]:
                    extraProps.extra_filter_value,
                }}
                {...rest}
              >
                <AutocompleteInput
                  optionText={extraProps.reference_source}
                  readOnly={disabled}
                  validate={is_required ? [required()] : undefined}
                />
              </ReferenceInput>
            )}
        </>
      )}
    </FormDataConsumer>
  );

  return (
    <>
      {component}


    </>
  );
};


export const ReferenceFieldInputSearchNewWatchxxx = ({
  field_meta,
  component,
  index,
  css_class,
  full_width,
  is_required,
  source_name,
}) => {
  var metaData = {};

  // let metaData = JSON.parse(field_meta);

  if (field_meta) {
    try {
      metaData = JSON.parse(field_meta);
    } catch (error) {
      console.error("Invalid JSON in field_meta:", error);
    }
  }
  // Helper function to handle date parsing
  const parseDate = (dateString) => {
    if (!dateString) return undefined;
    if (dateString.toLowerCase() === "today") return moment().startOf('day').toDate();
    if (dateString.toLowerCase() === "yesterday") return moment().subtract(1, 'days').startOf('day').toDate();
    if (dateString.toLowerCase() === "tomorrow") return moment().add(1, 'days').startOf('day').toDate();
    return moment(dateString, "YYYY-MM-DD", true).isValid() ? moment(dateString).toDate() : undefined;
  };

  const minDate = parseDate(metaData.minDate);
  const maxDate = parseDate(metaData.maxDate);


  const { disabled } = useWatchPlateSearched(); // Use the custom hook


  component = (
    <DateTimeInput
      validate={is_required ? [required()] : undefined}
      variant="outlined"
      source={source_name}
      key={index}
      className={` ${css_class}  `}
      minDate={minDate}
      maxDate={maxDate}
      readOnly={disabled}

    />
  );

  return (
    <>
      {component}


    </>
  );
};



export const ReferenceFieldInputSingleSearchNewWatch = ({
  field_meta,
  component,
  index,
  css_class,
  full_width,
  is_required,
  source_name,
  watched_column,
}) => {
  let extraProps = JSON.parse(field_meta);
  const { disabled } = useWatchMainChanged(watched_column); // Use the custom hook

  // State to store search text with debounce effect
  const [searchText, setSearchText] = useState("");

  // Debounce function to update search text after delay (e.g., 1000ms)
  const handleSearchChange = debounce((value) => {
    setSearchText(value);
  }, 1000);

  component = (
    <FormDataConsumer
      source={source_name}
      fullWidth={full_width}
      className={`input-field ${css_class}`}
    >
      {({ formData, ...rest }) => (
        <>
          <ReferenceInput
            source={source_name}
            reference={extraProps.reference_resource}
            key={index}
            perPage={extraProps.limit}
            fullWidth
            {...rest}
          >
            <AutocompleteInput
              fullWidth
              optionText={extraProps.reference_source}
              readOnly={disabled}
              validate={is_required ? [required()] : undefined}
              onInputChange={(event, value) => {
                handleSearchChange(value);
              }} // Use onInputChange instead of onSearchChange
              filterToQuery={(searchText) => ({
                [`${extraProps.search_key || "name"}@_ilike`]: `%${searchText}%`,
              })}
            />
          </ReferenceInput>

        </>
      )}
    </FormDataConsumer>
  );

  return <>{component}</>;
};


export const ReferenceFieldInputNewWatch = ({
  source_name,
  field_meta,
  index,
  css_class,
  is_required,
  watched_column,
}) => {
  let extraProps = JSON.parse(field_meta);
  const { disabled } = useWatchMainChanged(watched_column);

  const { setValue, control } = useFormContext();

  // Watch the current field for changes
  const watchedValue = useWatch({ control, name: source_name });

  const previousValue = React.useRef(watchedValue);

  useEffect(() => {
    if (previousValue.current !== watchedValue) {
      previousValue.current = watchedValue;

      if (extraProps.resetFields && extraProps.resetFields.length > 0) {
        extraProps.resetFields.forEach((fieldToReset) => {
          setValue(fieldToReset, null);
        });
      }
    }
  }, [watchedValue, extraProps.resetFields, setValue]);

  return (
    <ReferenceInput
      perPage={extraProps.limit}
      source={source_name}
      reference={extraProps.reference_resource}
      fullWidth
      key={index}
      className={`input-field ${css_class}`}
      sort={
        extraProps &&
        extraProps.sort_by && {
          field: extraProps.sort_by,
          order: extraProps.sort_order,
        }
      }
    >
      <AutocompleteInput
        fullWidth
        readOnly={disabled}
        optionText={extraProps.reference_source}
        validate={is_required ? [required()] : undefined}
        filterToQuery={(searchText) => ({
          [`${extraProps.reference_source}@_ilike`]: `%${searchText}%`,
        })}


      />
    </ReferenceInput>
  );
};

export const BooleanInputWatch = ({
  field_meta,
  source_name,
  index,
  css_class,
  is_required,
  watched_column,
}) => {
  const { disabled } = useWatchMainChanged(watched_column); // Use the custom hook

  let choices = [];
  try {
    choices = field_meta ? JSON.parse(field_meta) : [];
  } catch (error) {
    console.error("Error parsing field_meta:", error);
  }

  return (
    <>
      <AutocompleteInput
        source={source_name}
        fullWidth
        key={index}
        validate={is_required ? [required()] : undefined}
        choices={choices}
        readOnly={disabled}
        className={css_class}
      />

    </>
  );
};



export const BooleanInputWatchDriver = ({
  field_meta,
  source_name,
  index,
  css_class,
  is_required,
  watched_column,
}) => {
  const { disabled } = useWatchMainChanged(watched_column); // Use the custom hook
  const { control } = useFormContext();
  
  // Watch workflow_category to determine visibility
  const workflowCategory = useWatch({
    control,
    name: "workflow_category",
    defaultValue: null,
  });

  // Only show this component when workflow_category is "1" (driver)
  if (workflowCategory !== "1") {
    return null;
  }

  let choices = [];
  try {
    choices = field_meta ? JSON.parse(field_meta) : [];
  } catch (error) {
    console.error("Error parsing field_meta:", error);
  }

  return (
    <>
      <AutocompleteInput
        source={source_name}
        fullWidth
        key={index}
        validate={is_required ? [required()] : undefined}
        choices={choices}
        readOnly={disabled}
        className={css_class}
      />

    </>
  );
};


export const BooleanInputFilter = (props) => {
  const { field_meta, source_name, css_class, is_required, index } = props;




  let choices = [];
  try {
    choices = field_meta ? JSON.parse(field_meta) : [];
  } catch (error) {
    console.error("Error parsing field_meta:", error);
  }

  return (
    <SelectInput
      source={source_name}
      fullWidth
      key={index}
      validate={is_required ? [required()] : undefined}
      choices={choices}
      className={css_class}

    />

  );
};






export const TrainingInputWatch = ({
  source_name,
  index,
  css_class,
  is_required,
  watched_column,
}) => {
  const dataProvider = useDataProvider();
  const { setValue, getValues } = useFormContext();

  const { disabled } = useWatchMainChanged(watched_column);
  const driver_nid = useWatch({ name: "driver_nid" });
  const accident_time = useWatch({ name: "accident_time" });

  const choices = [
    { id: "نعم", name: "نعم" },
    { id: "لا", name: "لا" },
  ];

  // Use `useLayoutEffect` instead of `useEffect` to avoid state updates while rendering.
  useLayoutEffect(() => {
    // If either driver_nid or accident_time is missing, we return early
    if (!driver_nid || !accident_time) {
      setValue(source_name, "لا");
      return;
    }

    // Format the accident_time before querying dataProvider
    const formattedAccidentTime = moment(accident_time).format("YYYY-MM-DD");

    // Fetch training history based on the current driver_nid and accident_time
    dataProvider
      .getList("training_history", {
        filter: {
          "nid@_eq": driver_nid,
          "actual_date@_lte": formattedAccidentTime,
          "pass_status@_eq": "ناجح",
        },
        pagination: { page: 1, perPage: 1 },
      })
      .then(({ total }) => {
        const newValue = total > 0 ? "نعم" : "لا";
        setValue(source_name, newValue); // Set the value based on the total training history
      })
      .catch((error) => {
        console.error("Error fetching training data:", error);
        setValue(source_name, "لا"); // Set to "لا" in case of error
      });
  }, [driver_nid, accident_time, dataProvider, setValue, source_name]);

  return (
    <>
      <SelectInput
        source={source_name}
        fullWidth
        key={index}
        validate={is_required ? [required()] : undefined}
        choices={choices}
        readOnly={disabled}
        className={css_class}
        value={getValues(source_name)} // Ensure value is correctly passed from useFormContext
      />
    </>
  );
};



export const IsOdsWatchMainOds = ({
  field_meta,
  source_name,
  index,
  css_class,
  is_required,
}) => {
  const parsedMeta = React.useMemo(() => {
    try {
      return field_meta ? JSON.parse(field_meta) : [];
    } catch (error) {
      console.error("Error parsing field_meta:", error);
      return [];
    }
  }, [field_meta]);

  const initialValue = parsedMeta.find(choice => choice.selected)?.id || "";

  const [isDisabled, setIsDisabled] = useState(initialValue === "notcorrect");

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => {
        const value = formData[source_name] || initialValue;

        useEffect(() => {
          setIsDisabled(value === "notcorrect");
        }, [value]);

        return (
          <SelectInput
            source={source_name}
            fullWidth
            key={index}
            validate={is_required ? [required()] : undefined}
            choices={parsedMeta}
            disabled={isDisabled}
            className={css_class}
            defaultValue={initialValue}
            {...rest}
          />
        );
      }}
    </FormDataConsumer>
  );
};

export const MarkDownWatch = (meta) => {
  const { source_name, index, is_required, watched_column } = meta;
  const { disabled } = useWatchMainChanged(watched_column);
  const { setValue, getValues } = useFormContext();

  useEffect(() => {
    if (disabled) {
      setValue(source_name, getValues(source_name) || "");
    }
  }, [disabled, source_name, setValue, getValues]);

  return (
    <>
      {!disabled && <MarkdownInput
        fullWidth
        validate={is_required ? [required()] : undefined}
        source={source_name}
        key={index}
      />}

      {disabled && <MarkdownField
        fullWidth
        validate={is_required ? [required()] : undefined}
        source={source_name}
        key={index}
      />}

    </>
  );
};


export const TextInputWatch = (meta) => {
  const { source_name, index, is_required, watched_column } = meta;
  const { disabled } = useWatchMainChanged(watched_column);
  const { setValue, getValues } = useFormContext();

  useEffect(() => {
    if (disabled) {
      setValue(source_name, getValues(source_name) || "");
    }
  }, [disabled, source_name, setValue, getValues]);

  return (
    <>
      <TextInput
        fullWidth
        validate={is_required ? [required()] : undefined}
        source={source_name}
        key={index}
        readOnly={disabled}
      />

    </>
  );
};

export const NumberInputWatch = (meta) => {
  const { source_name, index, is_required, watched_column, field_meta } = meta;
  const { disabled } = useWatchMainChanged(watched_column);
  const { setValue, getValues } = useFormContext();
  var extraProps = JSON.parse(field_meta);

  /*   useEffect(() => {
      if (disabled) {
        setValue(source_name, getValues(source_name) || "");
      }
    }, [disabled, source_name, setValue, getValues]); */

  return (
    <>
      <NumberInput
        fullWidth
        validate={is_required ? [required(), number()] : [number()]}
        source={source_name}
        {...extraProps}
        key={index}
        readOnly={disabled}
        InputProps={{
          onWheel: (event) => event.target.blur(), // explicitly handle scroll event here
        }}

      />

    </>
  );
};

export const DateInputWatch = (meta) => {
  const { source_name, index, is_required, watched_column, field_meta } = meta;
  const { setValue } = useFormContext();

  let config = field_meta ? JSON.parse(field_meta) : {};

  const extraWatchColumn = config?.extra_watch_column || null;
  const valueToMatch = config?.value_to_match;
  const valueNotMatch = config?.value_not_match;
  const allowConvert = config?.allow_convert || false;
  const convertedColumn = config?.converted_column || null;

  const { disabled } = useWatchMainChanged(watched_column);
  const extraValue = extraWatchColumn ? useWatch({ name: extraWatchColumn }) : null;
  const [currentExtraValue, setCurrentExtraValue] = useState(extraValue);

  useEffect(() => {
    if (extraValue !== undefined) {
      setCurrentExtraValue(extraValue);
    }
  }, [extraValue]);

  let showTextField = false;
  if (extraWatchColumn) {
    if (valueToMatch !== undefined && currentExtraValue === valueToMatch) {
      showTextField = true;
    }
    if (valueNotMatch !== undefined && currentExtraValue !== valueNotMatch) {
      showTextField = true;
    }
  }

  const {
    field: { value, onChange },
    fieldState: { error },
  } = useInput({ source: source_name, validate: is_required ? [required()] : undefined });

  const handleDateChange = (newDate) => {
    if (newDate) {
      setValue(source_name, newDate);

      const gregorianMomentObject = moment(newDate);
      if (allowConvert && convertedColumn && gregorianMomentObject.isValid()) {
        const hijriDate = hMoment(gregorianMomentObject).format("iYYYY-iMM-iDD");
        setValue(convertedColumn, hijriDate);
      }
    }
  };

  return showTextField ? (
    <>
      <TextInput
        fullWidth
        validate={is_required ? [required()] : undefined}
        source={source_name}
        key={index}
        readOnly={true}
      />

    </>
  ) : (
    <>
      <DateInput
        fullWidth
        variant="outlined"
        validate={is_required ? [required()] : undefined}
        source={source_name}
        key={index}
        readOnly={disabled}
        onChange={handleDateChange}

        mask="yyyy-MM-dd"
        slotProps={{
          desktopPaper: { dir: "rtl" },
          mobilePaper: { dir: "rtl" },
        }}
      />
    </>
  );
};

export const DateInputWatchDriver = (meta) => {
  const { source_name, index, is_required, watched_column, field_meta } = meta;
  const { setValue, control } = useFormContext();

  let config = field_meta ? JSON.parse(field_meta) : {};

  const extraWatchColumn = config?.extra_watch_column || null;
  const valueToMatch = config?.value_to_match;
  const valueNotMatch = config?.value_not_match;
  const allowConvert = config?.allow_convert || false;
  const convertedColumn = config?.converted_column || null;

  // All hooks must be called before any conditional logic
  const { disabled } = useWatchMainChanged(watched_column);
  const extraValue = extraWatchColumn ? useWatch({ name: extraWatchColumn }) : null;
  const [currentExtraValue, setCurrentExtraValue] = useState(extraValue);

  // Watch workflow_category to determine visibility
  const workflowCategory = useWatch({
    control,
    name: "workflow_category",
    defaultValue: null,
  });

  const {
    field: { value, onChange },
    fieldState: { error },
  } = useInput({ 
    source: source_name, 
    validate: is_required ? [required()] : undefined 
  });

  // useEffect must also be called before conditional returns
  useEffect(() => {
    if (extraValue !== undefined) {
      setCurrentExtraValue(extraValue);
    }
  }, [extraValue]);

  // Only show this component when workflow_category is "1" (driver)
  if (workflowCategory !== "1") {
    return null;
  }

  let showTextField = false;
  if (extraWatchColumn) {
    if (valueToMatch !== undefined && currentExtraValue === valueToMatch) {
      showTextField = true;
    }
    if (valueNotMatch !== undefined && currentExtraValue !== valueNotMatch) {
      showTextField = true;
    }
  }

  const handleDateChange = (newDate) => {
    if (newDate) {
      setValue(source_name, newDate);

      const gregorianMomentObject = moment(newDate);
      if (allowConvert && convertedColumn && gregorianMomentObject.isValid()) {
        const hijriDate = hMoment(gregorianMomentObject).format("iYYYY-iMM-iDD");
        setValue(convertedColumn, hijriDate);
      }
    }
  };

  return showTextField ? (
    <>
      <TextInput
        fullWidth
        validate={is_required ? [required()] : undefined}
        source={source_name}
        key={index}
        readOnly={true}
      />

    </>
  ) : (
    <>
      <DateInput
        fullWidth
        variant="outlined"
        validate={is_required ? [required()] : undefined}
        source={source_name}
        key={index}
        readOnly={disabled}
        onChange={handleDateChange}

        mask="yyyy-MM-dd"
        slotProps={{
          desktopPaper: { dir: "rtl" },
          mobilePaper: { dir: "rtl" },
        }}
      />
    </>
  );
};

export const HijriDateInputWatch = ({
  source_name,
  index,
  is_required,
  watched_column,
  field_meta,
}) => {
  const { setValue, getValues, register } = useFormContext();
  const translate = useTranslate();
  const resource = useResourceContext();

  let config = field_meta ? JSON.parse(field_meta) : {};
  const extraWatchColumn = config?.extra_watch_column || null;
  const valueToMatch = config?.value_to_match;
  const valueNotMatch = config?.value_not_match;
  const allowConvert = config?.allow_convert || false;
  const convertedColumn = config?.converted_column || null;

  const { disabled } = useWatchMainChanged(watched_column);
  const extraValue = extraWatchColumn ? useWatch({ name: extraWatchColumn }) : null;
  const [currentExtraValue, setCurrentExtraValue] = useState(extraValue);

  useEffect(() => {
    if (extraValue !== undefined) {
      setCurrentExtraValue(extraValue);
    }
  }, [extraValue]);

  let showTextField = false;
  if (extraWatchColumn) {
    if (valueToMatch !== undefined && currentExtraValue === valueToMatch) {
      showTextField = true;
    }
    if (valueNotMatch !== undefined && currentExtraValue !== valueNotMatch) {
      showTextField = true;
    }
  }

  const {
    field: { value, onChange },
    fieldState: { error },
  } = useInput({ 
    source: source_name, 
    validate: is_required ? [required("هذا الحقل مطلوب")] : undefined 
  });

  // Fix: Better handling of Hijri date parsing and validation
  const parseHijriValue = (val) => {
    if (!val) return null;
    try {
      const hijriMoment = hMoment(val, "iYYYY-iMM-iDD", true);
      return hijriMoment.isValid() ? hijriMoment : null;
    } catch (error) {
      console.warn("Invalid Hijri date:", val, error);
      return null;
    }
  };

  const hijriMoment = parseHijriValue(value);

  const handleDateChange = (newDate) => {
    try {
      if (newDate && hMoment.isMoment(newDate)) {
        // Ensure we have a valid moment object
        if (newDate.isValid()) {
          const hijriDate = newDate.format("iYYYY-iMM-iDD");
          onChange(hijriDate);
          setValue(source_name, hijriDate);

          // Convert to Gregorian if needed
          if (allowConvert && convertedColumn) {
            try {
              const gregorianDate = newDate.format("YYYY-MM-DD");
              setValue(convertedColumn, gregorianDate);
            } catch (conversionError) {
              console.warn("Error converting to Gregorian:", conversionError);
            }
          }
        }
      } else if (newDate === null || newDate === undefined) {
        // Handle clearing the date
        onChange("");
        setValue(source_name, "");
        if (allowConvert && convertedColumn) {
          setValue(convertedColumn, "");
        }
      }
    } catch (error) {
      console.error("Error handling date change:", error);
      // Don't crash, just log the error
    }
  };

  const minHijriDate = hMoment("1356-01-02", "iYYYY-iMM-iDD");
  const maxHijriDate = hMoment().add(1, "day");
  
  // Get the label from translations
  const label = translate(`resources.${resource}.fields.${source_name}`, { _: source_name });
  
  return showTextField ? (
    <>
      <TextInput
        fullWidth
        validate={is_required ? [required()] : undefined}
        source={source_name}
        key={index}
        disabled={true}
      />
      <input
        type="hidden"
        {...register(source_name)}
        value={getValues(source_name) || ""}
      />
    </>
  ) : (
    <>
      <LocalizationProvider dateAdapter={AdapterMomentHijri}>
        <DesktopDatePicker
          label={label}
          value={hijriMoment}
          onChange={handleDateChange}
          format="iYYYY-iMM-iDD"
          minDate={minHijriDate}
          maxDate={maxHijriDate}
          readOnly={disabled}
          slotProps={{ 
            textField: { 
              variant: "outlined",
              fullWidth: true,
              error: !!error,
              helperText: error?.message,
           
              InputProps: {
                style: { 
                  direction: 'rtl', 
                  textAlign: 'right',
                  height: '40px'
                }
              },
              inputProps: {
                placeholder: "iYYYY-iMM-iDD",
                style: { textAlign: 'right' }
              }
            },
            desktopPaper: { dir: "rtl" },
            mobilePaper: { dir: "rtl" }
          }}
        />
      </LocalizationProvider>
    </>
  );
};

export const useWatchVehiclePlateInput = (name) => {
  const { control } = useFormContext();

  const editingVehicleMeta = useWatch({
    control,
    name: "editingVehicleMeta",
    defaultValue: false,
  });

  const VehicleMetaData = useWatch({
    control,
    name: "Vehicle_meta_data",
    defaultValue: null,
  });

  return {
    disabled: !editingVehicleMeta,
    VehicleMetaData: VehicleMetaData,
    control,
  };
};

export const TextInputWatchVehicle = (meta) => {
  const { disabled, VehicleMetaData } = useWatchVehiclePlateInput(
    "editingVehicleMeta"
  );
  const { source_name, index, is_required } = meta;

  if (!VehicleMetaData) {
    return null;
  }
  return (
    <TextInput
      fullWidth
      validate={is_required ? [required()] : undefined}
      source={source_name}
      key={index}
      disabled={disabled}
    />
  );
};

export const IsOdsChangeWatchVehicle = (meta) => {
  const { VehicleMetaData } = useWatchVehiclePlateInput(
    "editingVehicleMeta"
  );
  const { source_name, index, is_required } = meta;

  if (!VehicleMetaData) {
    return null;
  }
  return (
    <SelectInput
      fullWidth
      validate={is_required ? [required()] : undefined}
      source={source_name}
      key={index}
      disabled={true}
      choices={[
        { id: true, name: "نعم" },
        { id: false, name: "لا" },
      ]}
      optionText="name"
      optionValue="id"
    />
  );
};

export const DateTimeInputWithWatch = (meta) => {
  const { source_name, index, is_required, css_class } = meta;

  const plateNumber = useWatch({ control, name: "plate_number", defaultValue: "" });
  const isSearchDone = !!plateNumber;

  return (
    <Box display="flex" alignItems="center" gap={1} width="100%">
      <DateTimeInput
        fullWidth
        validate={is_required ? [required()] : undefined}
        variant="outlined"
        source={source_name}
        key={index}
        className={` ${css_class}  `}
        disabled={isSearchDone}
        InputProps={{ style: { direction: 'rtl', textAlign: 'right' } }}


      />
    </Box>
  );
};


const HiddenInputForDisabledField = ({ name, control }) => {
  const watchedValue = useWatch({ name, control });
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={watchedValue || ""}
      render={({ field }) => (
        <input type="hidden" {...field} value={watchedValue || ""} />
      )}
    />
  );
};


export const useWatchValueThreshold = (watched_column, threshold = 85) => {
  const { control } = useFormContext();

  const watchedValue = useWatch({
    control,
    name: watched_column,
    defaultValue: null,
  });

  const parsedValue = parseFloat(watchedValue);
  const isActive = !isNaN(parsedValue) && parsedValue < threshold;

  return {
    disabled: !isActive, // Disable if value >= threshold or not a valid number
    watchedValue: parsedValue,
    control,
  };
};


export const TextInputWatchThreshold = (meta) => {
  const { source_name, index, is_required, watched_column, field_meta } = meta;
  const translate = useTranslate();
  const resource = useResourceContext();
  
  // Parse threshold from field_meta if provided, default to 85
  let threshold = 85;
  if (field_meta) {
    try {
      const parsed = JSON.parse(field_meta);
      if (parsed.threshold !== undefined) {
        threshold = parseFloat(parsed.threshold);
      }
    } catch (e) {
      console.error('Error parsing field_meta for threshold:', e);
    }
  }

  const { disabled, watchedValue } = useWatchValueThreshold(watched_column, threshold);
  const { setValue, getValues } = useFormContext();

  // Get translated field label
  const watchedFieldLabel = translate(`resources.${resource}.fields.${watched_column}`, { _: watched_column });

  useEffect(() => {
    if (disabled) {
      // Clear or preserve value when disabled
      setValue(source_name, getValues(source_name) || "");
    }
  }, [disabled, source_name, setValue, getValues]);

  return (
    <>
      <TextInput
        fullWidth
        validate={is_required ? [required()] : undefined}
        source={source_name}
        key={index}
        readOnly={disabled}
        disabled={disabled}
        helperText={disabled ? `هذا الحقل نشط فقط عندما ${watchedFieldLabel} < ${threshold}` : undefined}
      />
    </>
  );
};
